import { ChevronLeft, Print } from '@mui/icons-material';
import {
    Box,
    Button,
    Card,
    Stack,
    styled,
    Table,
    TableRow,
    useTheme,
} from '@mui/material';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import AppPagination from 'components/AppPagination';
import Scrollbar from 'components/ScrollBar';
import columnShape from 'page-sections/operator/workshop-detail-participant/columnShape';
import HeadingArea from 'page-sections/operator/workshop-detail-participant/heading-area';
import SearchArea from 'page-sections/operator/workshop-detail-participant/search-area';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    useAsyncDebounce,
    useGlobalFilter,
    usePagination,
    useRowSelect,
    useSortBy,
    useTable,
} from 'react-table';
import axiosInstance from 'utils/axios';
import PrintBadge from 'utils/PrintBadge';
const HeadTableCell = styled(TableCell)(({ theme }) => ({
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    '&:first-of-type': {
        paddingLeft: 24,
    },
    '&:last-of-type': {
        paddingRight: 24,
    },
}));
const BodyTableCell = styled(HeadTableCell)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: 13,
    fontWeight: 500,
    borderBottom: `1px solid ${theme.palette.divider}`,
}));
const PurchasedWorkshopDetail = () => {
    const [listData, setListData] = useState([]);
    const [exportData, setExportData] = useState([]);
    const [workshopData, setWorkshopData] = useState([]);
    const theme = useTheme();
    const columns = useMemo(() => columnShape, []);
    const navigate = useNavigate();
    const { id } = useParams();
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        pageOptions,
        gotoPage,
        state,
        setGlobalFilter,
    } = useTable(
        { columns, data: listData },
        useGlobalFilter,
        useSortBy,
        usePagination,
        useRowSelect,
    ); // handle change pagination

    const getData = async (param) => {
        setExportData([]);
        await axiosInstance
            .get(`/api/prv/operator/workshops/${id}`)
            .then(({ data }) => {
                setListData(data.dataParticipant);
                setWorkshopData(data.workshopData);
                setExportData(
                    data.dataParticipant.map((item, index) => ({
                        No: index + 1,
                        Invoice_Number: item.number,
                        Full_Name: item.user.fullName,
                        Nik: item.user.nik,
                        Profession: item.user.profession,
                        Phone_Number: item.user.phoneNumber,
                        Email: item.user.email,
                        Institution: item.user.institution,
                    })),
                );
            })
            .catch((err) => console.log(err));
    };
    useEffect(() => {
        getData();
        // eslint-disable-next-line
    }, []);
    const handleChange = (_, currentPageNo) => gotoPage(currentPageNo - 1); // handle change for tab list
    const [searchValue, setSearchValue] = useState(state.globalFilter);
    const handleSearch = useAsyncDebounce(
        (value) => setGlobalFilter(value || undefined),
        200,
    );

    const handlePrintBadges = async () => {
        const readTrimmed = (value) => String(value ?? '').trim();

        const badges = (Array.isArray(listData) ? listData : [])
            .map((item) => ({
                nickName: item?.user?.nickName ?? '',
                qrCode: item?.user?.qrCode ?? '',
            }))
            .filter((badge) => readTrimmed(badge?.qrCode));

        if (!badges.length) return;
        await PrintBadge(badges);
    };

    return (
        <Box pt={2} pb={4}>
            <Button
                disableRipple
                startIcon={<ChevronLeft />}
                onClick={() => navigate(-1)}
                sx={{ mb: 2 }}
            >
                Back
            </Button>
            <Card
                sx={{
                    py: 2,
                }}
            >
                <Box px={3}>
                    <HeadingArea data={workshopData} />

                    <SearchArea
                        value={searchValue}
                        onChange={handleSearch}
                        setValue={setSearchValue}
                        data={exportData}
                        workshop={workshopData}
                        actions={
                            <Button
                                variant="contained"
                                startIcon={<Print />}
                                onClick={handlePrintBadges}
                                disabled={!listData?.length}
                            >
                                Print Badges
                            </Button>
                        }
                    />
                </Box>
                <Scrollbar autoHide={true}>
                    <Table
                        {...getTableProps()}
                        sx={{
                            minWidth: 900,
                        }}
                    >
                        <TableHead
                            sx={{
                                borderBottom: `1px solid ${theme.palette.divider}`,
                            }}
                        >
                            {headerGroups.map((headerGroup, index) => (
                                <TableRow
                                    key={index}
                                    {...headerGroup.getHeaderGroupProps()}
                                >
                                    {headerGroup.headers.map(
                                        (column, index) => (
                                            <HeadTableCell
                                                key={index}
                                                {...column.getHeaderProps(
                                                    column.getSortByToggleProps(),
                                                )}
                                                sx={{
                                                    paddingY: 0,
                                                    fontSize: 14,
                                                    fontWeight: 600,
                                                    borderBottom: 0,
                                                    width: column.width,
                                                    color: 'text.secondary',
                                                    minWidth: column.minWidth,
                                                    maxWidth: column.maxWidth,
                                                }}
                                            >
                                                {column.render('Header')}
                                            </HeadTableCell>
                                        ),
                                    )}
                                </TableRow>
                            ))}
                        </TableHead>

                        <TableBody {...getTableBodyProps()}>
                            {page.map((row, index) => {
                                prepareRow(row);
                                return (
                                    <TableRow
                                        {...row.getRowProps()}
                                        key={index}
                                    >
                                        {row.cells.map((cell, index) => {
                                            return (
                                                <BodyTableCell
                                                    key={index}
                                                    {...cell.getCellProps()}
                                                >
                                                    {cell.render('Cell')}
                                                </BodyTableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Scrollbar>

                <Stack alignItems="center" marginY={4}>
                    <AppPagination
                        shape="rounded"
                        onChange={handleChange}
                        count={pageOptions.length}
                    />
                </Stack>
            </Card>
        </Box>
    );
};

export default PurchasedWorkshopDetail;
